/**
 * API contract types matching the LockerServer Laravel response
 * envelope. Server-side canonical source: `App\Lib\Concerns\HasJsonResponse`.
 */

export enum SupportedApiMethods {
  GET = 'GET',
  PUT = 'PUT',
  PATCH = 'PATCH',
  POST = 'POST',
  DELETE = 'DELETE',
}

/** Lowercase axios method names. Used internally by sendable. */
export type HttpMethod = 'get' | 'post' | 'patch' | 'put' | 'delete'

/**
 * Common fields on every BE response envelope:
 *
 *   { responseCode: 200, message: 'ok', errors?, traceInfo? }
 *
 * Sits at the top of the wire-type hierarchy — both single-resource
 * (`ApiResponse<T>`) and paginated (`ApiPaginatedResponse<T>`)
 * inherit from this, so envelope fields aren't repeated.
 */
export interface ApiEnvelope {
  responseCode: number
  message: string
  errors?: Record<string, string>
  traceInfo?: StackTraceFrame[]
}

/**
 * Single-resource wire shape — envelope + a `data` payload of type T.
 *
 *   { responseCode, message, data: { ... } }
 *
 * `data` is optional because error responses carry the envelope
 * without a payload (e.g. 422 with `errors` populated, no `data`).
 */
export interface ApiResponse<T> extends ApiEnvelope {
  data?: T
}

export interface StackTraceFrame {
  file?: string
  line?: number
  function?: string
  class?: string
  type?: string
}

/**
 * Pagination request shape — every list endpoint accepts these
 * query params. Server-side: `HasPaginationInRequest` validates
 * page/size/sort/search; `IndexRequest` auto-derives filter rules
 * per resource from the model's `Queryable::filterableFields()`.
 *
 * `filter` is a record keyed by FE column name. The composable
 * serializing this object flattens `filter` into top-level query
 * params at request time (`{state: 'ACTIVE'}` → `?state=ACTIVE`)
 * so the BE's whitelist-on-validate gate sees individual fields.
 */
export interface PaginatedRequest {
  page?: number
  size?: number
  search?: string
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
  // Array values become a server-side `whereIn` (bulk-id / subtree / token filters).
  filter?: Record<string, string | number | boolean | null | undefined | number[] | string[]>
}

/**
 * Compute a progressive set of page-size options around a base unit,
 * plus the default (= first option) bound to the same call so the
 * "default matches dropdown's initial selection" invariant is BY
 * CONSTRUCTION, not by `[0]` discipline at every call site.
 *
 * Formula: `options[i] = base × (startMultiplier + i)` for i in
 * [0, count). The first option doubles as `defaultSize`.
 *
 * Use case: views that render rows in a grid where each row holds a
 * fixed multiple of items. `base` IS the per-row capacity (the grid's
 * max column count); options always fill complete rows — no half-row
 * stragglers regardless of which size the operator picks.
 *
 * **Convention** (`startMultiplier: 3, count: 4`): every section's
 * smallest page is **3 rows worth**, then 4 / 5 / 6 rows. Operator
 * sees a useful default page on first load AND a clean growth path
 * (4 reasonable jumps before they should switch to search / filter
 * instead). The grid template's max-cols MUST match `base` so the
 * row math holds visually.
 *
 *   paginationOptions({ base: 4 })
 *     → { options: [12, 16, 20, 24], defaultSize: 12 }   // 3 rows × 4 cols
 *   paginationOptions({ base: 5 })
 *     → { options: [15, 20, 25, 30], defaultSize: 15 }   // 3 rows × 5 cols
 *   paginationOptions({ base: 3 })
 *     → { options: [9, 12, 15, 18], defaultSize: 9 }     // 3 rows × 3 cols
 *
 * Override `startMultiplier` only when a section's UX genuinely
 * needs a different floor (e.g. compact admin tables where 12 cards
 * is too many out of the gate).
 *
 *   // Grid view:
 *   const grid = paginationOptions({ base: 4 })  // 4-col grid → 4 cols max
 *   const crud = useResourceCrud(..., { pageSize: grid.defaultSize })
 *   <DataView :rows-per-page-options="grid.options" />
 */
export function paginationOptions(opts: {
  base: number
  startMultiplier?: number
  count?: number
}): { options: number[]; defaultSize: number } {
  // Default startMultiplier = 3 → first option is always "3 rows
  // worth", matching the project convention. The view's grid
  // template should declare max-cols = `base` so the row math
  // visually lines up.
  const start = opts.startMultiplier ?? 3
  const count = opts.count ?? 4
  const options = Array.from({ length: count }, (_, i) => opts.base * (start + i))
  return { options, defaultSize: options[0]! }
}

/**
 * Project-wide pagination — default page size + dropdown options for
 * views WITHOUT a grid-alignment constraint (lists, tables). Computed
 * via `paginationOptions` so the math + first-option-as-default
 * invariant stay consistent with grid-constrained views.
 *
 * Pinned to the BE's default in `HasPaginationInResults.php`
 * (`perPage: $validated['size'] ?? 10`) so a first render — where the
 * FE issues `GET /api/<resource>` with no `size` param — receives the
 * row count its `<Paginator rows>` binding reports.
 *
 * Grid-constrained views (Devices inventory's 4-column grid, future
 * matrix views) derive their own pagination via `paginationOptions`
 * — see `<DevicesInventoryView>` for the canonical call site.
 */
const PROJECT_PAGINATION = paginationOptions({
  base: 10,
  startMultiplier: 1,
})

export const DEFAULT_PAGE_SIZE = PROJECT_PAGINATION.defaultSize
export const PAGE_SIZE_OPTIONS: readonly number[] = PROJECT_PAGINATION.options

/**
 * Laravel `LengthAwarePaginator` metadata. Shape is FIXED by the
 * framework — same keys for every paginated endpoint regardless of
 * resource. BE wire reference:
 * `backend/tests/Feature/Framework/AsJsonPaginatorTest.php`.
 */
export interface PaginationMeta {
  current_page: number
  from: number | null
  last_page: number
  path: string
  per_page: number
  to: number | null
  total: number
}

export interface PaginationLinks {
  first: string | null
  last: string | null
  prev: string | null
  next: string | null
}

/**
 * Caller-facing shape for paginated endpoints. The API client
 * (`useAPI.callApi`) detects a paginated wire response (presence of
 * top-level `meta` + `links` + array `data`) and CONSTRUCTS this
 * object before handing it to the caller — envelope fields like
 * `responseCode`/`message` are stripped, the rest is preserved
 * verbatim.
 *
 *     const resp = await routes.DEVICES
 *       .index<PaginatedResponse<Device>>()
 *       .send()
 *     resp.data.forEach(...)
 *     total.value = resp.meta.total
 */
export interface PaginatedResponse<T> {
  data: T[]
  meta: PaginationMeta
  links: PaginationLinks
}

/**
 * Paginated wire shape — what actually arrives over HTTP before
 * `useAPI.callApi` strips the envelope. Composed by multi-extension
 * so envelope fields and the caller-facing siblings aren't repeated.
 *
 *   { responseCode, message, data: [...], meta, links, errors?, traceInfo? }
 *
 * Internal to the API client; callers see `PaginatedResponse<T>`.
 */
export interface ApiPaginatedResponse<T> extends ApiEnvelope, PaginatedResponse<T> {}

/**
 * Composable-facing paginated state — wire payload (`data`, `meta`,
 * `links`) plus runtime status (`loading`, `error`). Single object
 * the consumer binds to for both list rendering and load-state UI.
 *
 * Before the first successful fetch: `data` is `[]`, `meta` carries
 * zero-defaults (`total: 0`, etc.), `links` carries `null`s — so
 * consumers can read `result.meta.total` / `result.data.length`
 * without null-guards from the first paint.
 *
 * Distinct from `PaginatedResponse<T>` (which is the wire-derived
 * value the API client returns). `PaginatedResult<T>` is what the
 * composable exposes to its consumer as a reactive outlet.
 */
export interface PaginatedResult<T> {
  data: T[]
  meta: PaginationMeta
  links: PaginationLinks
  loading: boolean
  error: string | null
}

/**
 * Wire shape predicted from the caller's declared `TResp`. Lets the
 * axios boundary generic (`instance.get<WireFor<TResp>>(...)`)
 * document the exact body the wire will deliver, without leaking
 * `unknown` into the call line.
 *
 *   - `TResp = PaginatedResponse<Device>` → `ApiPaginatedResponse<Device>`
 *     (envelope + `data: Device[]` + `meta` + `links`)
 *   - anything else                        → `ApiResponse<TResp>`
 *     (envelope + `data?: TResp`)
 *
 * `useAPI.unwrapBody` discriminates at runtime by body shape and
 * narrows the return to `TResp`.
 */
export type WireFor<TResp> = TResp extends PaginatedResponse<infer Item>
  ? ApiPaginatedResponse<Item>
  : ApiResponse<TResp>
